import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MoldOrder } from '../../types/mold';
import MoldOrderStatus from '../shared/MoldOrderStatus';
import MoldOrderCardTypeLabel from '../shared/MoldOrderCardTypeLabel';
import SecondaryButton from '../shared/SecondaryButton';
import { getPriorityLabel } from '../MoldDetail/utils';
import { styleConfig } from '../../constants/design';
import { useAppState } from '../../context/AppStateContext';
import { getTitleFieldRenderer, getSubtitleFieldRenderer, getFieldLabel } from './fieldRenderers';

interface MoldOrderCardProps {
  moldOrder: MoldOrder;
}

const MoldOrderCard: React.FC<MoldOrderCardProps> = ({ moldOrder }) => {
  const navigate = useNavigate();
  const { getKanbanCardHeadlines, getKanbanCardConfig } = useAppState();

  const handleViewDetails = (): void => {
    navigate(`/mold-orders/${moldOrder.id}?source=list`);
  };

  const headlines = getKanbanCardHeadlines(moldOrder);
  const cardConfig = getKanbanCardConfig();
  const titleRenderer = getTitleFieldRenderer(cardConfig.titleField);
  const subtitleRenderer = getSubtitleFieldRenderer(cardConfig.subtitleField);
  const subtitleLabel = getFieldLabel(cardConfig.subtitleField);
  const bgColorClass = `bg-white`;

  return (
    <div
      className={`${bgColorClass} relative rounded-xl shadow-lg border border-gray-100 mb-3 cursor-pointer transition-all duration-300 hover:shadow-xl hover:border-gray-200 hover:-translate-y-1 bg-gradient-to-br from-white to-gray-50`}
    >
      <Link
        to={`/mold-orders/${moldOrder.id}?source=list`}
        aria-label={`เปิดใบงาน ${moldOrder.id}`}
        className="absolute inset-0 z-10 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      />
      {/* Header Section - Order ID, Mold ID, Status, Priority */}
      <div className="flex justify-between items-start p-4">
        <div className="flex items-center gap-3">
          <div className="pt-2 flex flex-row gap-2 items-center">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              ใบงาน #{moldOrder.id}
            </h3>
            <div className="text-xs font-medium text-gray-500 mt-0.5">
              {React.createElement(titleRenderer, {
                moldOrder,
                value: headlines.title,
                fieldName: cardConfig.titleField,
              })}
            </div>
          </div>
          {moldOrder.priority <= 100 && (
            <div className="px-2 py-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full shadow-lg">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                {getPriorityLabel(moldOrder.priority)}
              </span>
            </div>
          )}
        </div>
        <MoldOrderStatus moldOrder={moldOrder} />
      </div>
      {headlines.subtitle && String(headlines.subtitle).trim() && (
        <div className="px-4">
          <div className="bg-gray-100/50 p-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-700">
                  {subtitleLabel}:
                </span>
              </div>
              <span className="flex flex-wrap gap-1.5 text-xs font-bold text-gray-900 px-2 py-1">
                {React.createElement(subtitleRenderer, {
                  moldOrder,
                  value: headlines.subtitle,
                  fieldName: cardConfig.subtitleField,
                })}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 pb-3">
        <div className="p-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-700">ผู้รับผิดชอบ:</span>
            </div>
            <span className="text-xs font-bold text-gray-900 bg-white px-2 py-1 rounded-md shadow-sm">
              {moldOrder.assignedTo?.fullname || moldOrder.assignedTo?.username || '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Footer Section - Condensed */}
      <div className="flex justify-between items-center px-5 py-3 border-t border-gray-200 rounded-b-xl">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-700">ประเภท:</span>
          </div>
          <div className="flex-shrink-0">
            <MoldOrderCardTypeLabel moldOrder={moldOrder} />
          </div>
        </div>
        <div className="relative z-20">
          <SecondaryButton
            text="ดูรายละเอียด"
            onClick={handleViewDetails}
            className="bg-white hover:bg-gray-100 transition-colors"
          />
        </div>
      </div>
    </div>
  );
};

// Memoized so scrolling the virtualized list doesn't re-render already-mounted cards.
export default React.memo(MoldOrderCard);
